import { z } from 'zod';

import {
  HoldingUnitContainerKindSchema,
  HoldingUnitIdentityModeSchema,
  HoldingUnitProductKindSchema,
  HoldingUnitTypeSchema,
} from '../holding-units/index.js';
import { TareProfileModeSchema, TareProfileSummaryDtoSchema } from '../tare-profiles/index.js';
import {
  unitProfileCategoryValues,
  unitProfileFixedIdPolicyValues,
  unitProfileTemplateKeyValues,
  unitProfileValidationIssueCodeValues,
  unitProfileWorkflowValues,
  type CloneUnitProfileRequest,
  type CloneUnitProfileResponse,
  type CreateUnitProfileRequest,
  type CreateUnitProfileResponse,
  type GetUnitProfileResponse,
  type ListUnitProfileTemplatesResponse,
  type ListUnitProfileWorkflowOptionsRequest,
  type ListUnitProfileWorkflowOptionsResponse,
  type ListUnitProfilesByLocationRequest,
  type ListUnitProfilesByLocationResponse,
  type ResolvedUnitProfileSnapshotFields,
  type UnitProfileCapabilityPolicy,
  type UnitProfileDto,
  type UnitProfileTemplateDto,
  type UnitProfileValidationIssue,
  type UnitProfileValidationResult,
  type UnitProfileWorkflowOptionDto,
  type UpdateUnitProfileRequest,
  type UpdateUnitProfileResponse,
} from './unit-profiles.dto.js';

const timestampSchema = z.iso.datetime();
const codeSchema = z.string().trim().min(1).max(64);
const displayNameSchema = z.string().trim().min(1).max(120);
const descriptionSchema = z.string().trim().min(1).max(500).nullable();

export const UnitProfileCategorySchema = z.enum(unitProfileCategoryValues);
export const UnitProfileFixedIdPolicySchema = z.enum(unitProfileFixedIdPolicyValues);
export const UnitProfileTemplateKeySchema = z.enum(unitProfileTemplateKeyValues);
export const UnitProfileWorkflowSchema = z.enum(unitProfileWorkflowValues);
export const UnitProfileValidationIssueCodeSchema = z.enum(
  unitProfileValidationIssueCodeValues,
);

const capabilityPolicyShape = {
  canReceiveIntake: z.boolean(),
  canReceiveTransfer: z.boolean(),
  canTransferOut: z.boolean(),
  canAdjust: z.boolean(),
  canReady: z.boolean(),
  canShip: z.boolean(),
  canRetire: z.boolean(),
  canBuildProduct: z.boolean(),
  canHoldLeftover: z.boolean(),
  canReopen: z.boolean(),
  canDisassemble: z.boolean(),
} as const;

export const UnitProfileCapabilityPolicySchema: z.ZodType<UnitProfileCapabilityPolicy> =
  z.object(capabilityPolicyShape);
export const PartialUnitProfileCapabilityPolicySchema =
  z.object({
    canReceiveIntake: z.boolean().optional(),
    canReceiveTransfer: z.boolean().optional(),
    canTransferOut: z.boolean().optional(),
    canAdjust: z.boolean().optional(),
    canReady: z.boolean().optional(),
    canShip: z.boolean().optional(),
    canRetire: z.boolean().optional(),
    canBuildProduct: z.boolean().optional(),
    canHoldLeftover: z.boolean().optional(),
    canReopen: z.boolean().optional(),
    canDisassemble: z.boolean().optional(),
  }).strict();

export const UnitProfileTemplateDtoSchema: z.ZodType<UnitProfileTemplateDto> =
  z.object({
    templateKey: UnitProfileTemplateKeySchema,
    profileCategory: UnitProfileCategorySchema,
    unitType: HoldingUnitTypeSchema,
    identityMode: HoldingUnitIdentityModeSchema,
    containerKind: HoldingUnitContainerKindSchema,
    productKind: HoldingUnitProductKindSchema,
    fixedIdPolicy: UnitProfileFixedIdPolicySchema,
    displayName: displayNameSchema,
    description: descriptionSchema.optional(),
    maxCapabilities: UnitProfileCapabilityPolicySchema,
    defaultCapabilityPolicy: UnitProfileCapabilityPolicySchema,
    allowedTareModes: z.array(TareProfileModeSchema).min(1),
  });

export const UnitProfileDtoSchema: z.ZodType<UnitProfileDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  code: codeSchema,
  displayName: displayNameSchema,
  description: descriptionSchema.optional(),
  templateKey: UnitProfileTemplateKeySchema,
  profileCategory: UnitProfileCategorySchema,
  unitType: HoldingUnitTypeSchema,
  identityMode: HoldingUnitIdentityModeSchema,
  containerKind: HoldingUnitContainerKindSchema,
  productKind: HoldingUnitProductKindSchema,
  fixedIdPolicy: UnitProfileFixedIdPolicySchema,
  tareProfileId: z.uuid().nullable().optional(),
  capabilityPolicy: UnitProfileCapabilityPolicySchema,
  isActive: z.boolean(),
  displayOrder: z.number().int().min(0),
  revision: z.number().int().min(1),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const UnitProfileValidationIssueSchema: z.ZodType<UnitProfileValidationIssue> =
  z.object({
    code: UnitProfileValidationIssueCodeSchema,
    field: z.string().trim().min(1).optional(),
    message: z.string().trim().min(1),
  });

export const UnitProfileValidationResultSchema: z.ZodType<UnitProfileValidationResult> =
  z.object({
    isValid: z.boolean(),
    issues: z.array(UnitProfileValidationIssueSchema),
  });

export const ListUnitProfileTemplatesResponseSchema: z.ZodType<ListUnitProfileTemplatesResponse> =
  z.object({
    items: z.array(UnitProfileTemplateDtoSchema),
  });

export const ListUnitProfilesByLocationRequestSchema: z.ZodType<ListUnitProfilesByLocationRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      includeInactive: z.boolean().optional(),
      profileCategory: UnitProfileCategorySchema.optional(),
      templateKey: UnitProfileTemplateKeySchema.optional(),
    })
    .strict();

export const ListUnitProfilesByLocationResponseSchema: z.ZodType<ListUnitProfilesByLocationResponse> =
  z.object({
    items: z.array(UnitProfileDtoSchema),
  });

export const CreateUnitProfileRequestSchema: z.ZodType<CreateUnitProfileRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      code: codeSchema,
      displayName: displayNameSchema,
      description: descriptionSchema.optional(),
      templateKey: UnitProfileTemplateKeySchema,
      tareProfileId: z.uuid().nullable().optional(),
      capabilityPolicy: PartialUnitProfileCapabilityPolicySchema.optional(),
      isActive: z.boolean().optional(),
      displayOrder: z.number().int().min(0).optional(),
    })
    .strict();

export const CreateUnitProfileResponseSchema: z.ZodType<CreateUnitProfileResponse> =
  z.object({
    item: UnitProfileDtoSchema,
  });

export const UpdateUnitProfileRequestSchema: z.ZodType<UpdateUnitProfileRequest> =
  z
    .object({
      companyId: z.uuid(),
      unitProfileId: z.uuid(),
      displayName: displayNameSchema.optional(),
      description: descriptionSchema.optional(),
      tareProfileId: z.uuid().nullable().optional(),
      capabilityPolicy: PartialUnitProfileCapabilityPolicySchema.optional(),
      isActive: z.boolean().optional(),
      displayOrder: z.number().int().min(0).optional(),
    })
    .strict()
    .refine(
      (value) =>
        value.displayName !== undefined
        || value.description !== undefined
        || value.tareProfileId !== undefined
        || value.capabilityPolicy !== undefined
        || value.isActive !== undefined
        || value.displayOrder !== undefined,
      { message: 'At least one field must be provided to update unit profile' },
    );

export const UpdateUnitProfileResponseSchema: z.ZodType<UpdateUnitProfileResponse> =
  z.object({
    item: UnitProfileDtoSchema,
  });

export const GetUnitProfileResponseSchema: z.ZodType<GetUnitProfileResponse> =
  z.object({
    item: UnitProfileDtoSchema,
  });

export const CloneUnitProfileRequestSchema: z.ZodType<CloneUnitProfileRequest> =
  z
    .object({
      companyId: z.uuid(),
      sourceUnitProfileId: z.uuid(),
      code: codeSchema,
      displayName: displayNameSchema,
      description: descriptionSchema.optional(),
      tareProfileId: z.uuid().nullable().optional(),
      capabilityPolicy: PartialUnitProfileCapabilityPolicySchema.optional(),
      isActive: z.boolean().optional(),
      displayOrder: z.number().int().min(0).optional(),
    })
    .strict();

export const CloneUnitProfileResponseSchema: z.ZodType<CloneUnitProfileResponse> =
  z.object({
    item: UnitProfileDtoSchema,
  });

export const UnitProfileWorkflowOptionDtoSchema: z.ZodType<UnitProfileWorkflowOptionDto> =
  z.object({
    id: z.uuid(),
    code: codeSchema,
    displayName: displayNameSchema,
    description: descriptionSchema.optional(),
    templateKey: UnitProfileTemplateKeySchema,
    profileCategory: UnitProfileCategorySchema,
    unitType: HoldingUnitTypeSchema,
    identityMode: HoldingUnitIdentityModeSchema,
    containerKind: HoldingUnitContainerKindSchema,
    productKind: HoldingUnitProductKindSchema,
    fixedIdPolicy: UnitProfileFixedIdPolicySchema,
    tareProfileId: z.uuid().nullable().optional(),
    tareProfile: TareProfileSummaryDtoSchema.nullable().optional(),
    capabilityPolicy: UnitProfileCapabilityPolicySchema,
    displayOrder: z.number().int().min(0),
    revision: z.number().int().min(1),
  });

export const ListUnitProfileWorkflowOptionsRequestSchema: z.ZodType<ListUnitProfileWorkflowOptionsRequest> =
  z
    .object({
      companyId: z.uuid(),
      storeId: z.uuid(),
      locationId: z.uuid(),
      workflow: UnitProfileWorkflowSchema,
      includeInactive: z.boolean().optional(),
    })
    .strict();

export const ListUnitProfileWorkflowOptionsResponseSchema: z.ZodType<ListUnitProfileWorkflowOptionsResponse> =
  z.object({
    items: z.array(UnitProfileWorkflowOptionDtoSchema),
  });

export const ResolvedUnitProfileSnapshotFieldsSchema: z.ZodType<ResolvedUnitProfileSnapshotFields> =
  z.object({
    unitProfileId: z.uuid(),
    unitProfileRevision: z.number().int().min(1),
    unitProfileDisplayName: displayNameSchema,
    unitProfileTemplateKey: UnitProfileTemplateKeySchema,
    profileCategory: UnitProfileCategorySchema,
    unitType: HoldingUnitTypeSchema,
    identityMode: HoldingUnitIdentityModeSchema,
    containerKind: HoldingUnitContainerKindSchema,
    productKind: HoldingUnitProductKindSchema,
    fixedIdPolicy: UnitProfileFixedIdPolicySchema,
    capabilityPolicy: UnitProfileCapabilityPolicySchema,
  });

export const unitProfileSchemaLimits = {
  maxCodeLength: 64,
  maxDisplayNameLength: 120,
  maxDescriptionLength: 500,
} as const;
